import type { ReactNode } from "react";
import type React from "react";
import { useEffect, useRef, createContext, useCallback, useContext } from "react";
import type { Blocker, BlockerFunction } from "react-router-dom";
import { useBlocker } from "react-router-dom";
import ConfirmNavigate from "~/components/ConfirmNavigate/ConfirmNavigate";
type BlockerPredicate = () => boolean;
type UnregisterNavigationBlocker = () => void;
type RegisterNavigationBlocker = (navigationBlocker: BlockerPredicate) => UnregisterNavigationBlocker;
const registerNavigationBlockerContext = createContext<RegisterNavigationBlocker | null>(null);
function useNavigationBlockerState() {
    //We need the navigation blocker state in general to be synchronous, so this
    //needs to be a ref.
    const navigationBlockersRef = useRef<BlockerPredicate[]>([]);
    const registerNavigationBlocker: RegisterNavigationBlocker = useCallback((navigationBlocker) => {
        navigationBlockersRef.current = [...navigationBlockersRef.current, navigationBlocker];
        return () => {
            navigationBlockersRef.current = navigationBlockersRef.current.filter((x) => x !== navigationBlocker);
        };
    }, []);
    return { navigationBlockersRef, registerNavigationBlocker };
}
export function NavigationBlockerProvider({ children }: {
    children: React.ReactNode;
}) {
    const { navigationBlockersRef, registerNavigationBlocker } = useNavigationBlockerState();
    const shouldBlock: BlockerFunction = useCallback((options) => {
        const { currentLocation, nextLocation } = options;
        // Only block when enabled and the pathname has changed
        // This is so we can ignore filter changes which only modify the query string for the current path.
        return nextLocation.pathname !== currentLocation.pathname && navigationBlockersRef.current.some((blocker) => blocker());
    }, [navigationBlockersRef]);
    const blocker = useBlocker(shouldBlock);
    return (<RegisterNavigationBlockerProvider registerNavigationBlocker={registerNavigationBlocker}>
            {children}
            <NavigateAwayPrompt blocker={blocker}/>
        </RegisterNavigationBlockerProvider>);
}
function NavigateAwayPrompt({ blocker }: {
    blocker: Blocker;
}) {
    return <ConfirmNavigate open={blocker.state === "blocked"} onCancelNavigation={() => blocker.reset?.()} onContinueNavigating={() => blocker.proceed?.()}/>;
}
export function RegisterNavigationBlockerProvider({ children, registerNavigationBlocker }: {
    registerNavigationBlocker: RegisterNavigationBlocker;
    children: ReactNode;
}) {
    return <registerNavigationBlockerContext.Provider value={registerNavigationBlocker}>{children}</registerNavigationBlockerContext.Provider>;
}
export function useNavigationBlocker({ when }: {
    when: boolean;
}) {
    const registerNavigationBlocker = useContext(registerNavigationBlockerContext);
    if (registerNavigationBlocker === null) {
        throw new Error("RegisterNavigationBlockerProvider not found");
    }
    const whenRef = useRef(when);
    whenRef.current = when;
    const navigationBlocker = useCallback<BlockerPredicate>(() => whenRef.current, []);
    useEffect(() => registerNavigationBlocker(navigationBlocker), [navigationBlocker, registerNavigationBlocker]);
}
